import { useEffect, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import './ServiceInfo.scss';
import { GLANCE_HEADLINE, GlanceGrid } from './serviceGlance';

// 앱 화면은 전부 하드림교회 샘플 데이터로 각 RN 앱을 iOS 시뮬레이터에서 실행해 캡처한 실제 화면 (2026-09-26)
import memberWorship from '../../images/apps/member/worship.jpg';
import memberNews from '../../images/apps/member/news.jpg';
import memberNewsDetail from '../../images/apps/member/news-detail.jpg';
import memberSchedule from '../../images/apps/member/schedule.jpg';
import memberBible from '../../images/apps/member/bible.jpg';
import memberBibleChapter from '../../images/apps/member/bible-chapter.jpg';
import memberSermon from '../../images/apps/member/sermon.jpg';
import memberGallery from '../../images/apps/member/gallery.jpg';
import memberGalleryDetail from '../../images/apps/member/gallery-detail.jpg';
import adminWorship from '../../images/apps/admin/worship.jpg';
import adminWorshipItems from '../../images/apps/admin/worship-items.jpg';
import adminNews from '../../images/apps/admin/news.jpg';
import adminNewsForm from '../../images/apps/admin/news-form.jpg';
import adminSchedule from '../../images/apps/admin/schedule.jpg';
import adminSermon from '../../images/apps/admin/sermon.jpg';
import adminGallery from '../../images/apps/admin/gallery.jpg';
import adminStaffApproval from '../../images/apps/admin/staff-approval.jpg';
import rollMembers from '../../images/apps/roll/members.jpg';
import rollMemberDetail from '../../images/apps/roll/member-detail.jpg';
import rollAttendance from '../../images/apps/roll/attendance.jpg';
import rollVisitation from '../../images/apps/roll/visitation.jpg';
import financeList from '../../images/apps/finance/list.jpg';
import financeForm from '../../images/apps/finance/form.jpg';
// 주일학교 앱 화면은 2026-10-03 부서·진입 모드·승인 단계·부서일정·갤러리 반영 후 다시 캡처
import ssEntryMode from '../../images/apps/sundayschool/entry-mode.jpg';
import ssSignupLeader from '../../images/apps/sundayschool/signup-leader.jpg';
import ssTeacherDashboard from '../../images/apps/sundayschool/teacher-dashboard.jpg';
import ssAttendanceCheck from '../../images/apps/sundayschool/attendance-check.jpg';
import ssNotices from '../../images/apps/sundayschool/notices.jpg';
import ssParentHome from '../../images/apps/sundayschool/parent-home.jpg';
import ssStudentDetail from '../../images/apps/sundayschool/student-detail.jpg';
import ssParentChild from '../../images/apps/sundayschool/parent-child.jpg';
import ssSchedule from '../../images/apps/sundayschool/schedule.jpg';
import ssGalleryAlbum from '../../images/apps/sundayschool/gallery-album.jpg';
import ssApprovals from '../../images/apps/sundayschool/approvals.jpg';
import ssLeaderApprovals from '../../images/apps/sundayschool/leader-approvals.jpg';

const PROCESS = [
  {
    num: '01',
    title: 'Discovery',
    desc: '규모와 기능만 알려주시고, 교회의 필요를 요청하세요.',
  },
  {
    num: '02',
    title: 'Craft',
    desc: '교회 로고 · 컬러 · 이미지에 맞춘 커스텀 앱 제작.',
  },
  {
    num: '03',
    title: 'Launch',
    desc: 'App Store · Google Play 동시 출시 후 평생 무료 유지보수.',
  },
];

// 서비스소개는 앱 단위로 나눈다: 성도 앱 / 앱관리 앱 / 교적부 앱 / 재정 앱 / 주일학교 앱 (2026-09-25 관리자 앱 3분할 반영)
export type ServicePart = 'member' | 'admin' | 'roll' | 'finance' | 'sundayschool';

export const SERVICE_PARTS: { part: ServicePart; label: string; path: string }[] = [
  { part: 'member', label: '성도 앱', path: '/service/member' },
  { part: 'admin', label: '앱관리 앱', path: '/service/admin' },
  { part: 'roll', label: '교적부 앱', path: '/service/roll' },
  { part: 'finance', label: '재정 앱', path: '/service/finance' },
  { part: 'sundayschool', label: '주일학교 앱', path: '/service/sundayschool' },
];

const PAGE_SUB: Record<ServicePart, string> = {
  member:
    '예배순서부터 갤러리까지 — 성도의 신앙생활을 돕는 6가지 서비스를 소개합니다.',
  admin:
    '예배순서 · 소식 · 일정 · 설교 · 갤러리부터 담당자 승인까지 — 성도 앱을 운영하는 앱관리 앱을 소개합니다.',
  roll:
    '교적 · 출석 · 심방까지 — 성도 한 사람 한 사람을 돌보는 교적부 앱을 소개합니다.',
  finance:
    '재정 권한을 받은 담당자만 사용하는, 교회 재정 전용 앱을 소개합니다.',
  sundayschool:
    '부서별 출석 · 알림장 · 일정 · 갤러리까지 — 부서담당자, 교사, 학부모를 잇는 주일학교 앱을 소개합니다.',
};

type Shot = { src: string; alt: string };

type Feature = {
  num: string;
  eyebrow: string;
  title: ReactNode;
  desc: string;
  list: string[];
  shots: Shot[];
};

type PartContent = { eyebrow: string; label: string; features: Feature[] };

const PARTS: Record<ServicePart, PartContent> = {
  member: {
    eyebrow: 'Part 01',
    label: '성도 앱 — 성도를 위한 서비스',
    features: [
      {
        num: '01',
        eyebrow: 'Worship Order',
        title: (
          <>
            이번 주 <span className="em">예배</span>,
            <br />
            손 안의 주보.
          </>
        ),
        desc: '이번 주 예배 순서가 앱에 바로 올라옵니다. 같은 날 1부 · 2부 예배를 골라 보고, 화살표로 지난주 예배 순서도 다시 볼 수 있습니다.',
        list: ['1부 · 2부 · 3부 예배별 순서', '지난주 예배 다시 보기', '찬양 가사 · 신앙고백 본문 함께 보기'],
        shots: [{ src: memberWorship, alt: '하드림교회 성도 앱 예배순서 화면' }],
      },
      {
        num: '02',
        eyebrow: 'Church News',
        title: (
          <>
            <span className="em">소식</span>이 흐르는,
            <br />
            교회의 광장.
          </>
        ),
        desc: '공지사항, 행사안내, 교우소식이 한 곳에 모입니다. 행사는 일시 · 장소 · 참고 사항까지 한 화면에 정리되고, 새 소식에는 NEW 표시가 붙습니다.',
        list: ['공지사항 · 행사안내 · 교우소식 분류', '제목 · 내용 검색', '행사 일시 · 장소 · 참고 사항 안내'],
        shots: [
          { src: memberNews, alt: '하드림교회 성도 앱 교회소식 목록 화면' },
          { src: memberNewsDetail, alt: '하드림교회 성도 앱 교회소식 상세 화면' },
        ],
      },
      {
        num: '03',
        eyebrow: 'Church Calendar',
        title: (
          <>
            교회의 한 해를,
            <br />
            <span className="em">한 줄</span>로 넘겨 보다.
          </>
        ),
        desc: '지난 일정부터 앞으로 1년의 일정까지 월별로 이어서 보여줍니다. 공휴일도 함께 표시되어 교회 일정과 연휴를 한눈에 맞춰 볼 수 있습니다.',
        list: ['월별로 이어지는 일정 목록', '공휴일 함께 표시', '여러 날 일정 · 종일 일정'],
        shots: [{ src: memberSchedule, alt: '하드림교회 성도 앱 교회일정 화면' }],
      },
      {
        num: '04',
        eyebrow: 'Bible Reading',
        title: (
          <>
            매일의 <span className="em">말씀</span>,
            <br />
            언제든 펼쳐 읽다.
          </>
        ),
        desc: '개역개정과 새번역을 오가며 성경 66권을 읽습니다. 매일성경연구로 한 본문을 일주일 동안 깊이 묵상할 수도 있습니다.',
        list: ['개역개정 · 새번역', '구약 · 신약 책 목록과 검색', '매일성경연구 (DBS 5단계)'],
        shots: [
          { src: memberBible, alt: '하드림교회 성도 앱 성경 목록 화면' },
          { src: memberBibleChapter, alt: '하드림교회 성도 앱 시편 23장 화면' },
        ],
      },
      {
        num: '05',
        eyebrow: 'Sermon Video',
        title: (
          <>
            <span className="em">설교</span>가 필요한 곳,
            <br />
            어디에서든.
          </>
        ),
        desc: '이번 주 설교가 맨 위에 걸리고, 지난 설교는 목록으로 쌓입니다. 영상을 누르면 교회가 올린 설교 영상으로 바로 이동합니다.',
        list: ['이번 주 설교 강조', '본문 · 설교자 · 재생 시간 표시', '설교 영상 바로 열기'],
        shots: [{ src: memberSermon, alt: '하드림교회 성도 앱 설교영상 화면' }],
      },
      {
        num: '06',
        eyebrow: 'Gallery',
        title: (
          <>
            함께한 <span className="em">순간들</span>이,
            <br />한 권의 앨범으로.
          </>
        ),
        desc: '수련회, 예배, 봉사의 순간을 앨범으로 모아 봅니다. 성도가 올린 사진은 담당자가 승인한 뒤에 갤러리에 올라갑니다.',
        list: ['앨범별 사진 정리', '분류별 앨범 보기', '성도 사진 업로드 · 담당자 승인'],
        shots: [
          { src: memberGallery, alt: '하드림교회 성도 앱 갤러리 앨범 목록 화면' },
          { src: memberGalleryDetail, alt: '하드림교회 성도 앱 갤러리 앨범 화면' },
        ],
      },
    ],
  },
  admin: {
    eyebrow: 'Part 02',
    label: '앱관리 앱 — 성도 앱 운영',
    features: [
      {
        num: '07',
        eyebrow: 'Worship Admin',
        title: (
          <>
            예배 순서를,
            <br />
            <span className="em">손 안</span>에서 발행하다.
          </>
        ),
        desc: '날짜별로 예배를 만들고, 지난 예배를 복제해 순서를 빠르게 채웁니다. 발행하는 순간 성도 앱에 이번 주 예배가 올라갑니다.',
        list: ['날짜 · 예배명별 예배 추가 · 복제', '순서 항목 추가 · 순서 변경', '발행 · 발행 취소'],
        shots: [
          { src: adminWorship, alt: '하드림교회 앱관리 앱 예배순서 관리 화면' },
          { src: adminWorshipItems, alt: '하드림교회 앱관리 앱 예배 순서 항목 화면' },
        ],
      },
      {
        num: '08',
        eyebrow: 'News Admin',
        title: (
          <>
            교회의 <span className="em">소식</span>을,
            <br />
            바로 올리고 고치다.
          </>
        ),
        desc: '공지 · 행사 · 교우소식을 휴대폰에서 바로 쓰고 고칩니다. 행사 일시와 장소, 참고 사항까지 입력하면 성도 앱에 그대로 보입니다.',
        list: ['소식 등록 · 수정 · 삭제', '분류별 보기 · 검색', '행사 정보 · 말씀 구절 입력'],
        shots: [
          { src: adminNews, alt: '하드림교회 앱관리 앱 교회소식 관리 화면' },
          { src: adminNewsForm, alt: '하드림교회 앱관리 앱 소식 수정 화면' },
        ],
      },
      {
        num: '09',
        eyebrow: 'Calendar & Sermon',
        title: (
          <>
            일정과 설교도,
            <br />
            <span className="em">한 앱</span>에서.
          </>
        ),
        desc: '교회 일정을 등록하면 공휴일과 함께 성도 앱에 표시됩니다. 설교영상은 영상 주소만 넣으면 목록에 올라가고, 순서와 상단 안내도 바로 바꿉니다.',
        list: ['교회일정 등록 · 수정 · 삭제', '설교영상 등록 · 순서 변경', '상단 히어로 문구 수정'],
        shots: [
          { src: adminSchedule, alt: '하드림교회 앱관리 앱 교회일정 관리 화면' },
          { src: adminSermon, alt: '하드림교회 앱관리 앱 설교영상 관리 화면' },
        ],
      },
      {
        num: '10',
        eyebrow: 'Gallery Admin',
        title: (
          <>
            올라온 사진은,
            <br />
            <span className="em">확인</span>한 뒤에 게시.
          </>
        ),
        desc: '성도가 올린 사진은 승인 대기 목록에 먼저 모입니다. 담당자가 승인하면 갤러리에 게시되고, 반려하면 게시되지 않습니다.',
        list: ['성도 업로드 승인 · 반려', '앨범 만들기 · 사진 올리기', '분류별 앨범 관리'],
        shots: [{ src: adminGallery, alt: '하드림교회 앱관리 앱 갤러리 관리 화면' }],
      },
      {
        num: '11',
        eyebrow: 'Staff & Permissions',
        title: (
          <>
            맡은 사람에게,
            <br />
            맡은 <span className="em">만큼</span>만.
          </>
        ),
        desc: '담당자는 가입 후 최종관리자의 승인을 받아야 앱을 쓸 수 있습니다. 재정처럼 민감한 정보는 최종관리자가 권한을 준 담당자에게만 열립니다.',
        list: ['담당자 가입 승인 · 거절', '재정 권한 개별 부여 · 회수', '한 계정으로 교적부 · 재정 앱 로그인'],
        shots: [{ src: adminStaffApproval, alt: '하드림교회 앱관리 앱 담당자 승인관리 화면' }],
      },
    ],
  },
  roll: {
    eyebrow: 'Part 03',
    label: '교적부 앱 — 교적 · 출석 · 심방',
    features: [
      {
        num: '12',
        eyebrow: 'Member Registry',
        title: (
          <>
            성도 한 사람 한 사람,
            <br />
            <span className="em">기록</span>하고 <span className="em">기억</span>하다.
          </>
        ),
        desc: '전체 교인과 새가족, 이번 주 생일자를 한눈에 봅니다. 성도마다 직분 · 부서 · 세례 · 등록일이 한 프로필에 담기고, 전화와 문자도 바로 연결됩니다.',
        list: ['교인 수 · 새가족 · 생일 요약', '부서별 명부 · 이름 검색', '성도 상세 · 전화 · 문자 바로 연결'],
        shots: [
          { src: rollMembers, alt: '하드림교회 교적부 앱 교적관리 화면' },
          { src: rollMemberDetail, alt: '하드림교회 교적부 앱 성도 상세 화면' },
        ],
      },
      {
        num: '13',
        eyebrow: 'Attendance',
        title: (
          <>
            이번 주 예배,
            <br />
            누가 <span className="em">함께</span>했나요.
          </>
        ),
        desc: '날짜와 예배를 고르고 성도마다 출석 · 온라인 · 결석을 한 번에 누릅니다. 출석 인원은 화면 위에 바로 집계됩니다.',
        list: ['출석 · 온라인 · 결석 3단계 체크', '주일 · 수요 · 금요 · 새벽 예배별 기록', '날짜별 출석 집계'],
        shots: [{ src: rollAttendance, alt: '하드림교회 교적부 앱 출석부 화면' }],
      },
      {
        num: '14',
        eyebrow: 'Pastoral Care',
        title: (
          <>
            심방의 발걸음을,
            <br />
            <span className="em">기록</span>으로 남기다.
          </>
        ),
        desc: '정기 · 신규 · 병원 심방까지, 누가 언제 누구를 찾아갔는지 월별로 기록합니다. 다가오는 심방은 예정으로 표시되어 놓치지 않습니다.',
        list: ['월별 심방 기록', '심방 구분 · 심방자 · 내용', '완료 · 예정 현황'],
        shots: [{ src: rollVisitation, alt: '하드림교회 교적부 앱 심방관리 화면' }],
      },
    ],
  },
  finance: {
    eyebrow: 'Part 04',
    label: '재정 앱 — 재정 권한 담당자 전용',
    features: [
      {
        num: '15',
        eyebrow: 'Finance',
        title: (
          <>
            <span className="em">재정</span>의 투명함,
            <br />
            숫자로 증명하다.
          </>
        ),
        desc: '헌금과 지출을 항목별로 기록하면 월별 수입 · 지출 · 잔액이 바로 계산됩니다. 재정 권한을 받은 담당자만 들어오는 별도 앱이라, 교적이나 콘텐츠 담당자에게 재정 내역이 노출되지 않습니다.',
        list: ['월별 수입 · 지출 · 잔액', '십일조 · 감사헌금 · 운영비 등 항목별 기록', '재정 권한 담당자 전용 앱'],
        shots: [
          { src: financeList, alt: '하드림교회 재정 앱 월별 내역 화면' },
          { src: financeForm, alt: '하드림교회 재정 앱 내역 수정 화면' },
        ],
      },
    ],
  },
  sundayschool: {
    eyebrow: 'Part 05',
    label: '주일학교 앱 — 부서담당자 · 교사 · 학부모를 잇는 서비스',
    features: [
      {
        num: '16',
        eyebrow: 'Departments & Roles',
        title: (
          <>
            부서를 고르고,
            <br />
            <span className="em">내 자리</span>에서 시작하다.
          </>
        ),
        desc: '유초등부 · 중고등부처럼 부서를 고르면 반 · 출석 · 알림장이 모두 그 부서 기준으로 나뉩니다. 부서담당자 · 스텝, 교사 · 리더, 학부모 중 내 자리를 고르면 꼭 필요한 화면만 열립니다.',
        list: ['부서별로 나뉘는 반 · 학생 · 알림장', '부서담당자 · 교사 · 학부모 3가지 화면', '교사는 맡을 반을 골라 가입 신청'],
        shots: [
          { src: ssEntryMode, alt: '하드림교회 주일학교 앱 진입 모드 선택 화면' },
          { src: ssSignupLeader, alt: '하드림교회 주일학교 앱 교사 회원가입 화면' },
        ],
      },
      {
        num: '17',
        eyebrow: 'Department Dashboard',
        title: (
          <>
            오늘 챙길 아이들이,
            <br />
            <span className="em">한 화면</span>에.
          </>
        ),
        desc: '오늘 출석률과 남은 출석 체크, 확인 안 한 알림장, 오늘 생일인 아이까지 부서 대시보드에 모입니다. 최근 4주 출석률과 3주 이상 장기결석도 자동으로 계산됩니다.',
        list: ['오늘 출석률 · 남은 체크', '최근 4주 출석률', '장기결석 · 생일 · 오늘 할 일'],
        shots: [{ src: ssTeacherDashboard, alt: '하드림교회 주일학교 앱 부서 대시보드 화면' }],
      },
      {
        num: '18',
        eyebrow: 'Class Attendance',
        title: (
          <>
            아이가 도착하면,
            <br />
            부모님께 <span className="em">알림</span>이.
          </>
        ),
        desc: '반별로 출석 · 지각 · 결석 · 공결을 체크하고 저장만 하면 됩니다. 저장하는 순간 학부모에게 도착 알림이 가고, 새친구 · 장기결석 아이는 표시로 바로 눈에 띕니다.',
        list: ['반별 출석 체크', '출석 · 지각 · 결석 · 공결', '저장 시 학부모 도착 알림'],
        shots: [{ src: ssAttendanceCheck, alt: '하드림교회 주일학교 앱 출석 체크 화면' }],
      },
      {
        num: '19',
        eyebrow: 'Students & Families',
        title: (
          <>
            아이의 <span className="em">신앙 여정</span>을,
            <br />
            가정과 함께.
          </>
        ),
        desc: '학생마다 신앙 여정과 보호자 연락처가 기록되고, 교사 메모는 학부모에게 공개되지 않습니다. 학부모는 “우리아이”에서 출석 이력과 신앙 여정을 보고, 담임 선생님께 바로 문의합니다.',
        list: ['학생별 신앙 여정 기록', '교사 메모 (학부모 비공개)', '우리아이 출석 이력 · 담임교사 문의'],
        shots: [
          { src: ssStudentDetail, alt: '하드림교회 주일학교 앱 학생 상세 화면' },
          { src: ssParentChild, alt: '하드림교회 주일학교 앱 우리아이 화면' },
        ],
      },
      {
        num: '20',
        eyebrow: 'Notices',
        title: (
          <>
            보낸 알림장,
            <br />
            <span className="em">확인</span>까지 챙기다.
          </>
        ),
        desc: '주보와 공지를 부서 전체 또는 반별로 보내고, 어느 가족이 확인했는지 받아봅니다. 학부모는 “확인했습니다”를 누르고, 확인하지 않은 가족에게는 한 번 더 보낼 수 있습니다.',
        list: ['부서 전체 · 반별 발송', '확인 · 미확인 가족 현황', '미확인 가족 재발송 (2일 경과 시 자동)'],
        shots: [
          { src: ssNotices, alt: '하드림교회 주일학교 앱 알림장 현황 화면' },
          { src: ssParentHome, alt: '하드림교회 주일학교 앱 학부모 홈 화면' },
        ],
      },
      {
        num: '21',
        eyebrow: 'Calendar & Gallery',
        title: (
          <>
            부서의 일정과 <span className="em">추억</span>을,
            <br />
            함께 나누다.
          </>
        ),
        desc: '소풍 · 교사 기도회 같은 부서 일정이 공휴일과 함께 월별로 이어집니다. 활동 사진은 부서 앨범에 모이고, 로그인한 교사와 학부모만 볼 수 있습니다.',
        list: ['부서일정 · 공휴일 함께 표시', '부서별 앨범 · 사진 올리기', '사진은 로그인한 가족 · 교사만 보기'],
        shots: [
          { src: ssSchedule, alt: '하드림교회 주일학교 앱 부서일정 화면' },
          { src: ssGalleryAlbum, alt: '하드림교회 주일학교 앱 갤러리 앨범 화면' },
        ],
      },
      {
        num: '22',
        eyebrow: 'Approval Chain',
        title: (
          <>
            가입 승인은,
            <br />
            <span className="em">바로 위</span> 단계에서.
          </>
        ),
        desc: '부서담당자는 교사 · 스텝을, 교사는 맡은 반 아이의 학부모를 승인합니다. 아이를 가장 잘 아는 사람이 확인하기 때문에, 모르는 사람이 아이 정보에 들어올 수 없습니다.',
        list: ['관리자 → 전체담당자 → 부서담당자 → 교사 · 스텝', '교사가 맡은 반 학부모 승인', '등급별로 보기 · 수정 권한 구분'],
        shots: [
          { src: ssApprovals, alt: '하드림교회 주일학교 앱 교사 가입 승인 화면' },
          { src: ssLeaderApprovals, alt: '하드림교회 주일학교 앱 학부모 가입 승인 화면' },
        ],
      },
    ],
  },
};

// 예전 링크 호환: /service#part01·#part02 해시 (2026-09-25 이전의 /service/church는 App.tsx에서 /service/admin으로 리다이렉트)
export function ServiceInfoRedirect() {
  const location = useLocation();
  const to = location.hash === '#part02' ? '/service/admin' : '/service/member';
  return <Navigate to={to} replace />;
}

export default function ServiceInfo({ part }: { part: ServicePart }) {
  const content = PARTS[part];

  useEffect(() => {
    const nodes = document.querySelectorAll('.service-info .reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [part]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [part]);

  return (
    <div className="service-info">
      <Header />

      <section className="page-header">
        <div className="container reveal">
          <div className="breadcrumb">
            <Link to="/">Home</Link> <span>· Service</span>
          </div>
          <h1 className="page-title">
            교회의 모든 순간,
            <br />
            앱 안에 <span className="em">담다</span>.
          </h1>
          <p className="page-sub">{PAGE_SUB[part]}</p>
          <div className="subtabs">
            {SERVICE_PARTS.map((item) => (
              <Link key={item.part} to={item.path} className={part === item.part ? 'active' : ''}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="part-header">
        <div className="container reveal">
          <span className="eyebrow">{content.eyebrow}</span>
          <span className="part-label">{content.label}</span>
          <p className="sample-note">모든 화면은 하드림교회를 샘플로 한 실제 앱 화면입니다.</p>
          {/* public/prototype/*.html — 앱별 단독 클릭형 프로토타입(정적 페이지라 Link 대신 a로 새 탭) */}
          <a
            className="sample-btn"
            href={`/prototype/${part}.html`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
              <path d="M10.5 18.5h3" />
            </svg>
            모바일 샘플 보기
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>

      {content.features.map((feature, index) => {
        const reversed = index % 2 === 1;
        return (
          <section key={feature.num} className={reversed ? 'feature reversed white-bg' : 'feature'}>
            <div className="container">
              <div className="feature-grid reveal">
                <div className="feature-visual">
                  <div className="visual-wrap">
                    <div className="visual-blob" />
                    <div className="screenshot-pair">
                      {feature.shots.map((shot) => (
                        <img key={shot.src} src={shot.src} alt={shot.alt} loading="lazy" />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="feature-text">
                  <span className="num-eyebrow">
                    {feature.num} / {feature.eyebrow}
                  </span>
                  <h2>{feature.title}</h2>
                  <p>{feature.desc}</p>
                  <ul className="feature-list">
                    {feature.list.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="glance">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow light">At a Glance</span>
            <h2>
              {GLANCE_HEADLINE.services},
              <br />
              <span className="em">{GLANCE_HEADLINE.apps}</span> 앱.
            </h2>
          </div>
          <GlanceGrid />
        </div>
      </section>

      <section className="process">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>
              3주면, 우리 교회의 <span className="em">앱</span>이 됩니다.
            </h2>
          </div>
          <div className="process-list reveal">
            {PROCESS.map((step) => (
              <div className="process-item" key={step.num}>
                <div className="process-num">{step.num}</div>
                <div className="process-title">{step.title}</div>
                <div className="process-desc">{step.desc}</div>
                <div className="process-arr" aria-hidden="true">
                  ——
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="final-cta" id="contact">
        <div className="container final-cta-inner reveal">
          <h2>
            이제, 우리 교회의 앱을
            <br />
            시작할 시간입니다.
          </h2>
          <p>신청 후, 우리 교회에 딱 맞는 견적을 받아보세요.</p>
           <Link to="/inquiry" className="btn-cream">
            신청하기 &nbsp;→
          </Link>
        </div>
      </section>

      <ScrollToTopButton />
      <Footer />
    </div>
  );
}
